import React, {
    FC, useCallback, useMemo, useState,
} from 'react';
import {
    Icon,
    Loader,
    Modal,
    Title,
} from 'ui-library';
import {
    ICON_ALERT_CIRCLE_SOLID,
    ICON_CHECK_CIRCLE_SOLID,
    ICON_DOCUMENT_LOADING,
    ICON_DOCUMENT_OUTLINE,
    ICON_DOCUMENT_RE_UPLOAD,
    ICON_DOWNLOAD_OUTLINE,
    ICON_TRIANGLE_OUTLINE,
    ICON_TYPE_CLOSE,
} from 'ui-library/components/Icon';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import UploadDocuments from 'components/UploadDocuments/UploadDocuments';
import Error from 'components/AtomicStructure/atoms/Error';
import { FILE_TYPES } from 'pages/ClientOnboardingFlow/pages/NewsPlan/UploadDocumentsPage/validation/const';
import {
    DocStatus,
} from './DocBox.types';
import './DocBox.css';

type DocBoxGenericProps = {
    docName: string;
    uploadStatus?: DocStatus;
    canUpload?: boolean;
    canDownload?: boolean;
    isDownloadInProgress?: boolean;
    onDownload?: () => void;
    className?: string;
    disabled?: boolean;
    numberOfFilesAllowed?: number;
    allowedFileTypes?: string;
    uploadModalOkText?: string;
    uploadInProgress?: boolean;
    uploadDisabled?: boolean;
    handleUpload?: ({ files }: { files: File[] }) => void;
};

const DocBoxGeneric: FC<DocBoxGenericProps> = ({
    uploadStatus = DocStatus.ReadyToGenerate,
    className,
    docName = 'Upload document',
    disabled,
    canUpload,
    canDownload,
    isDownloadInProgress,
    onDownload,
    numberOfFilesAllowed = 1,
    allowedFileTypes = FILE_TYPES.pdf,
    uploadModalOkText,
    uploadInProgress,
    uploadDisabled,
    handleUpload,
}) => {
    const {
        t,
        i18n: { language },
    } = useTranslation();

    const [showUploadModal, setShowUploadModal] = React.useState(false);
    const [files, setFiles] = useState<File[]>([]);
    const [error, setError] = useState<string>('');

    const classNames = useMemo(
        () => classnames(className, 'doc-box', {
            'doc-box-error': uploadStatus === DocStatus.Error,
            'doc-box-initial': disabled,
        }),
        [className, uploadStatus, disabled],
    );

    const uploadStatusIconType = useMemo(() => {
        switch (uploadStatus) {
            case DocStatus.ReadyToSubmit:
                return ICON_CHECK_CIRCLE_SOLID;
            case DocStatus.ReadyToUpload:
                return '';
            case DocStatus.ReadyToSign:
                return ICON_ALERT_CIRCLE_SOLID;
            case DocStatus.Error:
                return ICON_TRIANGLE_OUTLINE;
            case DocStatus.Loading:
                return ICON_DOCUMENT_LOADING;
            default:
                return '';
        }
    }, [uploadStatus]);

    const handleDocumentUpload = useCallback(async () => {
        if (!files.length) {
            setError(t('contactGroupDocuments.uploadDocumentsModal.noFilesSelectedError'));

            return;
        }
        setError('');

        if (typeof handleUpload === 'function') {
            handleUpload({ files });
            setShowUploadModal(false);
        }
    }, [files, handleUpload]);

    return (
        <>
            <div className="doc-wrapper">
                <div className={classNames}>
                    <div className="doc-box-inner">
                        <Icon
                            className={
                                uploadStatus === DocStatus.Loading
                                    ? 'loading-spinner'
                                    : ''
                            }
                            type={uploadStatusIconType}
                            size={28}
                        />
                        {![DocStatus.Loading, DocStatus.Error]?.includes(uploadStatus) && (
                            <Icon type={ICON_DOCUMENT_OUTLINE} size={28} />
                        )}
                        {uploadStatus !== DocStatus.ReadyToGenerate
                            && (uploadStatus === DocStatus.Error ? (
                                t(
                                    'ApplicationDetails.AdvisorySection.GenerateDocument.failed',
                                )
                            ) : (
                                <span>{docName}</span>
                            ))}
                    </div>

                    <div className="doc-box-inner doc-box-actions">
                        {canDownload
                            && (isDownloadInProgress ? (
                                <Loader />
                            ) : (
                                <span
                                    tabIndex={-1}
                                    aria-hidden="true"
                                    role="button"
                                    onClick={onDownload}
                                >
                                    <Icon
                                        type={ICON_DOWNLOAD_OUTLINE}
                                        size={28}
                                    />
                                </span>
                            ))}
                        {canUpload && (
                            <span
                                tabIndex={-2}
                                aria-hidden="true"
                                role="button"
                                onClick={() => setShowUploadModal(true)}
                            >
                                <Icon
                                    type={ICON_DOCUMENT_RE_UPLOAD}
                                    size={28}
                                />
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {showUploadModal && (
                <Modal
                    title=""
                    visible
                    okText={uploadModalOkText}
                    cancelText={t('documentSign.modal.documentIsNotSent.cancel')}
                    onOk={handleDocumentUpload}
                    okButtonProps={{
                        disabled: uploadInProgress || uploadDisabled,
                        loading: uploadInProgress,
                    }}
                    okType="primary"
                    cancelButtonProps={{ disabled: uploadInProgress }}
                    onCancel={() => setShowUploadModal(false)}
                    width={610}
                    className="upload-document-modal"
                    borderRadius
                    closable
                    closeIcon={<Icon type={ICON_TYPE_CLOSE} size={16} />}
                >
                    <Title type={2} className="font-normal mb-32">
                        <>
                            {t('ApplicationDetails.AdvisorySection.UploadDocument')}
                            {' '}
                            {docName}
                        </>
                    </Title>
                    <UploadDocuments
                        onSuccess={setFiles}
                        onError={setError}
                        numberOfFiles={numberOfFilesAllowed}
                        fileTypesAllowed={allowedFileTypes as string}
                    />
                    {error && <Error>{error}</Error>}
                </Modal>
            )}
        </>
    );
};

export default DocBoxGeneric;
